// React
import React, { useEffect, useRef } from 'react';

// Utils
import { getSocialPlatformIcon } from '../../utils/iconHelpers';
import { formatAntisemitismTypeLabel } from '../../utils/helpers';

// Styles
import '../../css/EntityCard.css';

const EntityCard = React.memo(({
  entity,
  isSelected,
  compactMode = false,
  onSelect,
  formatDate,
  isReported = false,
}) => {
  const cardRef = useRef(null);

  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [isSelected]);

  const handleClick = () => onSelect(String(entity.EntityId));

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(String(entity.EntityId));
    }
  };

  const rawName = entity.ProfileName || entity.DisplayName || `user_${entity.EntityId}`;
  const displayName = rawName.startsWith('@') ? rawName : `@${rawName}`;
  const descriptionText = entity.contentDesc || entity.DisplayName || entity.Text || 'No preview available';
  const createdDate = entity.CreatedDate ? formatDate(entity.CreatedDate) : null;

  const antisemitismLabel =
    Array.isArray(entity.AntisemitismTypes) && entity.AntisemitismTypes.length > 0
      ? entity.AntisemitismTypes.map(formatAntisemitismTypeLabel).join(', ')
      : null;

  const metaParts = [antisemitismLabel, entity.Language, createdDate].filter(Boolean);

  return (
    <div
      ref={cardRef}
      className={`entity-card ${isSelected ? 'selected' : ''} ${compactMode ? 'compact' : ''} ${isReported ? 'entity-card--reported' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={handleKeyPress}
    >
      {isReported && (
        <span className="database-pill database-pill-reported entity-card-reported-tag">
          Reported
        </span>
      )}
      <div className="entity-row-status">
        <span className={`entity-status-dot ${entity.IsRemoved ? 'removed' : 'active'}`} />
      </div>
      <div className="entity-row-content">
        <div className="entity-card-primary">
          <div className="entity-source-container">
            {getSocialPlatformIcon(entity.Source) && (
              <img
                src={getSocialPlatformIcon(entity.Source)}
                alt={entity.Source || ''}
                className="social-platform-icon"
              />
            )}
            <h4 className="entity-title">{displayName}</h4>
          </div>
        </div>
        <div className="entity-meta">
          {metaParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="entity-meta-dot" aria-hidden="true" />}
              <span className="entity-meta-item">{part}</span>
            </React.Fragment>
          ))}
        </div>
        <p className="entity-preview">{descriptionText}</p>
      </div>
    </div>
  );
});

EntityCard.displayName = 'EntityCard';

export default EntityCard;
